import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "../../../styles/InformationsForm.module.scss";
import axios from "axios";
import { debounce } from "lodash";

const API = "https://data.geopf.fr/geocodage/search/";

const AddressInput = ({ value, onChange, error }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickOutside = useCallback((event) => {
    if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
      setShowSuggestions(false);
      if (inputValue !== value && inputValue !== "") {
        setInputValue(value);
        return;
      }

      if (inputValue === "") {
        onChange("");
        return;
      }
    }
  }, [value, inputValue]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const fetchSuggestion = useCallback(debounce( async (search) => {
    if (!search) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const query = `?q=${encodeURIComponent(search)}`;
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}${query}`);
      const data = await response.data;

      if (data?.features) {
        const results = data.features.map((res) => ({
          city: res.properties?.city ?? "",
          postalCode: res.properties?.postcode ?? "",
          address: res.properties?.name ?? "",
          citycode: res.properties?.citycode ?? "",
          depCode: res.properties.context,
        }));
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error("Error fetching suggestions", err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, 500), []);

  const handleChange = useCallback((event) => {
    const value = event.target.value;
    setInputValue(value);
    if (value.length > 2) fetchSuggestion(value);
  }, [fetchSuggestion]);

  const handleSuggestionClick = (suggestion) => {
    const fullAddress = `${suggestion.address}, ${suggestion.postalCode} ${suggestion.city}`;
    setInputValue(fullAddress);
    onChange({ fullAddress: fullAddress, region: suggestion.depCode?.split(',')?.pop() });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className={styles.field}>
      <div className={styles.inputContainer} ref={autocompleteRef}>
        <input
          type="text"
          id="address"
          name="address"
          value={inputValue}
          onChange={handleChange}
          className={`${styles.input} ${inputValue ? styles.filled : ""} ${error ? styles.errorInput : ""}`}
          autoComplete="off"
          placeholder="Adresse, code postal, ville d'exercice"
          aria-autocomplete="list"
          aria-controls="address-suggestions"
        />
        <label htmlFor="address" className={`${styles.label} ${error ? styles.errorLabel : ""}`}>
          Adresse, code postal, ville d&apos;exercice{" "}
          <span className={styles.required}>*</span>
        </label>
        {isLoading && (
          <div className={styles.loader}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 50 50"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                strokeWidth="5"
                stroke="#555"
                strokeLinecap="round"
                strokeDasharray="90, 150"
                strokeDashoffset="0"
                transform="rotate(0 25 25)"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  dur="1.5s"
                  values="-35; 175"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </div>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <ul className={styles.suggestionsList} id="address-suggestions" role="listbox">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                className={styles.suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                role="option"
                aria-selected="false"
              >
                {`${suggestion.address}, ${suggestion.postalCode} ${suggestion.city}`}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <span className={styles.errorMessage}>{error}</span>}
    </div>
  );
};

export default AddressInput;